import { LoginTicket } from "google-auth-library";
import { BoardType, NotationType } from "common/unions";
import {
  NotationAttributes,
  NotationCreationAttributes,
  LineNotationAttributes,
  CurveNotationAttributes,
  SqrtNotationAttributes,
  CircleNotationAttributes,
  ConicNotationAttributes,
  FreeSketchNotationAttributes,
} from "common/baseTypes";
import {
  UserAttributes,
  UserCreationAttributes,
  StudentLessonCreationAttributes,
  StudentLessonAttributes,
} from "common/userTypes";
import { LessonAttributes, LessonCreationAttributes } from "common/lessonTypes";
import {
  QuestionAttributes,
  QuestionCreationAttributes,
} from "common/questionTypes";
import {
  PracticeQuestionListItem,
  PracticeCheckRequest,
  PracticeCheckResult,
  PracticeAskRequest,
  PracticeCoachRequest,
  PracticeCoachResult,
  PracticeCoachPhase,
  PracticeAiQuota,
  PracticePartsExtractRequest,
  PracticePartsExtractResult,
  PracticeProblemPart,
} from "common/practiceQuestionTypes";
import type { PracticeInstancePublic } from "common/practiceGenerators";
import type { PracticeLocale } from "common/practiceLocale";
import type { PracticeDifficulty } from "common/practiceQuestionTemplates";
import type { PracticeSkillStateRow } from "common/practiceMastery";
import type {
  PracticeShareOp,
  PracticeShareRecord,
  PracticeShareSnapshot,
} from "common/practiceSessionStore";
import { AnswerAttributes, AnswerCreationAttributes } from "common/answerTypes";
import axios, { AxiosError } from "axios";
import { usePracticeLocaleStore } from "../store/pinia/practiceLocaleStore";
import axiosHelper from "./axiosHelper";
import {
  isPracticeAiLimitError,
  practiceAiLimitMessage,
  formatPracticeAiFailureMessage,
} from "./guestPracticeHelper";
const { baseURL, imagesURL } = axiosHelper();

export class PracticeShareSeatTakenError extends Error {
  constructor() {
    super("seat taken");
    this.name = "PracticeShareSeatTakenError";
  }
}

export class PracticeAiLimitError extends Error {
  readonly limit?: number;
  readonly remaining?: number;
  constructor(message: string, limit?: number, remaining?: number) {
    super(message);
    this.name = "PracticeAiLimitError";
    this.limit = limit;
    this.remaining = remaining;
  }
}

/** @deprecated Use PracticeAiLimitError */
export const GuestAiLimitError = PracticeAiLimitError;

function practiceRequestLocale(): "en" | "he" {
  try {
    return usePracticeLocaleStore().tutorLocale;
  } catch {
    return "en";
  }
}

export default function useApiHelper() {
  async function log(message: string) {
    try {
      await axios.post(baseURL + "/log", { level: "error", message: message });
    } catch (error) {
      throw new Error(
        `Failed to log message: ${(error as AxiosError).message}`,
      );
    }
  }

  async function getPracticeAiQuota(): Promise<PracticeAiQuota> {
    const { data } = await axios.get<PracticeAiQuota>(
      baseURL + "/practice-ai-quota",
    );
    return data;
  }

  async function recognizeSketchOcr(
    imageBase64: string,
  ): Promise<{ symbol: string }> {
    try {
      const { data } = await axios.post<{ symbol: string }>(
        baseURL + "/ocr/sketch",
        { imageBase64 },
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>;
      const serverMessage =
        axiosError.response?.data?.message ?? axiosError.response?.data?.error;
      throw new Error(
        serverMessage
          ? `Sketch OCR failed: ${serverMessage}`
          : `Sketch OCR failed: ${axiosError.message}`,
      );
    }
  }

  async function checkPracticeWork(
    questionUUId: string,
    studentWork: string,
    problemImageBase64?: string,
    problemText?: string,
    parts?: PracticeProblemPart[],
    activePartId?: string,
    pathMeta?: { pathSkill?: string; subjectPercent?: number },
  ): Promise<PracticeCheckResult> {
    try {
      const { data } = await axios.post<PracticeCheckResult>(
        baseURL + "/practice-questions/" + questionUUId + "/check",
        {
          questionUUId,
          studentWork,
          ...(problemImageBase64
            ? { problemImageBase64 }
            : {}),
          ...(problemText ? { problemText } : {}),
          ...(parts?.length ? { parts } : {}),
          ...(activePartId ? { activePartId } : {}),
          locale: practiceRequestLocale(),
          ...(pathMeta?.pathSkill ? { pathSkill: pathMeta.pathSkill } : {}),
          ...(pathMeta?.subjectPercent != null
            ? { subjectPercent: pathMeta.subjectPercent }
            : {}),
        } satisfies PracticeCheckRequest,
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{
        message?: string;
        error?: string;
        limit?: number;
        remaining?: number;
      }>;
      if (
        isPracticeAiLimitError(
          axiosError.response?.status,
          axiosError.response?.data,
        )
      ) {
        throw new PracticeAiLimitError(
          practiceAiLimitMessage(axiosError.response?.data),
          axiosError.response?.data?.limit,
          axiosError.response?.data?.remaining,
        );
      }
      throw new Error(
        formatPracticeAiFailureMessage(
          "check",
          axiosError.response?.data?.message ?? axiosError.response?.data?.error,
          axiosError.message,
        ),
      );
    }
  }

  async function coachPracticeWork(
    questionUUId: string,
    studentWork: string,
    problemImageBase64?: string,
    problemText?: string,
    phase?: PracticeCoachPhase,
    parts?: PracticeProblemPart[],
    activePartId?: string,
    exampleFocus?: string,
  ): Promise<PracticeCoachResult> {
    try {
      const { data } = await axios.post<PracticeCoachResult>(
        baseURL + "/practice-questions/" + questionUUId + "/coach",
        {
          questionUUId,
          studentWork,
          ...(problemImageBase64
            ? { problemImageBase64 }
            : {}),
          ...(problemText ? { problemText } : {}),
          ...(phase ? { phase } : {}),
          ...(exampleFocus?.trim() ? { exampleFocus: exampleFocus.trim() } : {}),
          ...(parts?.length ? { parts } : {}),
          ...(activePartId ? { activePartId } : {}),
          locale: practiceRequestLocale(),
        } satisfies PracticeCoachRequest,
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{
        message?: string;
        error?: string;
        limit?: number;
        remaining?: number;
      }>;
      if (
        isPracticeAiLimitError(
          axiosError.response?.status,
          axiosError.response?.data,
        )
      ) {
        throw new PracticeAiLimitError(
          practiceAiLimitMessage(axiosError.response?.data),
          axiosError.response?.data?.limit,
          axiosError.response?.data?.remaining,
        );
      }
      throw new Error(
        formatPracticeAiFailureMessage(
          "coach",
          axiosError.response?.data?.message ?? axiosError.response?.data?.error,
          axiosError.message,
        ),
      );
    }
  }

  async function askPracticeQuestion(
    questionUUId: string,
    question: string,
    studentWork: string,
    problemImageBase64?: string,
    problemText?: string,
    parts?: PracticeProblemPart[],
    activePartId?: string,
  ): Promise<PracticeCoachResult> {
    try {
      const { data } = await axios.post<PracticeCoachResult>(
        baseURL + "/practice-questions/" + questionUUId + "/ask",
        {
          question,
          studentWork,
          ...(problemImageBase64 ? { problemImageBase64 } : {}),
          ...(problemText ? { problemText } : {}),
          ...(parts?.length ? { parts } : {}),
          ...(activePartId ? { activePartId } : {}),
          locale: practiceRequestLocale(),
        } satisfies PracticeAskRequest,
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{
        message?: string;
        error?: string;
        limit?: number;
        remaining?: number;
      }>;
      if (
        isPracticeAiLimitError(
          axiosError.response?.status,
          axiosError.response?.data,
        )
      ) {
        throw new PracticeAiLimitError(
          practiceAiLimitMessage(axiosError.response?.data),
          axiosError.response?.data?.limit,
          axiosError.response?.data?.remaining,
        );
      }
      throw new Error(
        formatPracticeAiFailureMessage(
          "coach",
          axiosError.response?.data?.message ?? axiosError.response?.data?.error,
          axiosError.message,
        ),
      );
    }
  }

  async function extractPracticeParts(
    questionUUId: string,
    problemImageBase64?: string,
    problemText?: string,
  ): Promise<PracticePartsExtractResult> {
    try {
      const { data } = await axios.post<PracticePartsExtractResult>(
        baseURL + "/practice-questions/" + questionUUId + "/parts",
        {
          ...(problemImageBase64 ? { problemImageBase64 } : {}),
          ...(problemText ? { problemText } : {}),
          locale: practiceRequestLocale(),
        } satisfies PracticePartsExtractRequest,
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{
        message?: string;
        error?: string;
        limit?: number;
        remaining?: number;
        parts?: PracticePartsExtractResult["parts"];
      }>;
      if (
        isPracticeAiLimitError(
          axiosError.response?.status,
          axiosError.response?.data,
        )
      ) {
        throw new PracticeAiLimitError(
          practiceAiLimitMessage(axiosError.response?.data),
          axiosError.response?.data?.limit,
          axiosError.response?.data?.remaining,
        );
      }
      throw new Error(
        formatPracticeAiFailureMessage(
          "coach",
          axiosError.response?.data?.message ?? axiosError.response?.data?.error,
          axiosError.message,
        ),
      );
    }
  }

  async function authGoogleUser(idToken: string): Promise<LoginTicket | null> {
    try {
      const { data } = await axios.post<string>(baseURL + "/gauth", {
        idToken: idToken,
      });
      return data as unknown as LoginTicket;
    } catch (error) {
      throw new Error(
        `Failed to authenticate Google user: ${(error as AxiosError).message}`,
      );
    }
  }

  // both local and goole
  // async function authUserByToken(): Promise<UserAttributes | undefined> {
  //   try {
  //     const res = await axios.get<UserAttributes>(baseURL + "/auth");
  //     return res?.data;
  //   } catch (error) {
  //     console.log(
  //       `Failed to authenticate user by token: ${
  //         (error as AxiosError).message
  //       }`,
  //     );
  //   }
  // }

  async function authLocalUserByPassword(
    email: string,
    password: string,
  ): Promise<UserAttributes | null> {
    try {
      const res = await axios.post<UserAttributes>(baseURL + `/auth`, {
        email: email,
        password: password,
      });
      return res?.data;
    } catch (error) {
      log(
        `Failed to authenticate user ${email}: ${
          (error as AxiosError).message
        }`,
      );
      return null;
    }
  }

  async function registerUser(
    user: UserCreationAttributes,
  ): Promise<UserAttributes> {
    try {
      const res = await axios.post<UserAttributes>(baseURL + "/users", user);
      return res?.data;
    } catch (error) {
      throw new Error(
        `Failed to register user: ${(error as AxiosError).message}`,
      );
    }
  }

  async function addLessonToSharedLessons(
    studentLesson: StudentLessonCreationAttributes,
  ) {
    try {
      const existingStudentLesson = await axios.get(
        baseURL +
          `/studentlessons?lessonUUId=${studentLesson.lesson.uuid}&userUUId=${studentLesson.user.uuid}`,
      );

      if (existingStudentLesson.data) return;

      return await axios.post<StudentLessonAttributes>(
        baseURL + "/studentlessons",
        studentLesson,
      );
    } catch (error) {
      throw new Error(
        `Failed to add lesson to shared lessons: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function addLesson(
    lesson: LessonCreationAttributes,
  ): Promise<LessonAttributes> {
    try {
      const newLesson = await axios.post<LessonAttributes>(
        baseURL + "/lessons",
        lesson,
      );
      return newLesson.data;
    } catch (error) {
      throw new Error(`Failed to add lesson: ${(error as AxiosError).message}`);
    }
  }

  async function updateLesson(
    uuid: string,
    name: string,
  ): Promise<LessonAttributes> {
    try {
      const { data } = await axios.put<LessonAttributes>(baseURL + "/lessons", {
        uuid,
        name,
      });
      return data;
    } catch (error) {
      throw new Error(
        `Failed to update lesson: ${(error as AxiosError).message}`,
      );
    }
  }

  async function deleteLesson(uuid: string): Promise<void> {
    try {
      await axios.delete(baseURL + "/lessons", { data: { uuid } });
    } catch (error) {
      throw new Error(
        `Failed to delete lesson: ${(error as AxiosError).message}`,
      );
    }
  }

  async function addQuestion(
    question: QuestionCreationAttributes,
  ): Promise<QuestionAttributes> {
    try {
      const newQuestion = await axios.post<QuestionAttributes>(
        baseURL + "/questions",
        question,
      );
      return newQuestion.data;
    } catch (error) {
      throw new Error(
        `Failed to add question: ${(error as AxiosError).message}`,
      );
    }
  }

  async function updateQuestion(
    uuid: string,
    name: string,
  ): Promise<QuestionAttributes> {
    try {
      const { data } = await axios.put<QuestionAttributes>(
        baseURL + "/questions",
        { uuid, name },
      );
      return data;
    } catch (error) {
      throw new Error(
        `Failed to update question: ${(error as AxiosError).message}`,
      );
    }
  }

  async function deleteQuestion(uuid: string): Promise<void> {
    try {
      await axios.delete(baseURL + "/questions", { data: { uuid } });
    } catch (error) {
      throw new Error(
        `Failed to delete question: ${(error as AxiosError).message}`,
      );
    }
  }

  async function addAnswer(
    answer: AnswerCreationAttributes,
  ): Promise<AnswerAttributes> {
    try {
      const { data } = await axios.post<AnswerAttributes>(
        baseURL + "/answers",
        answer,
      );
      return data;
    } catch (error) {
      throw new Error(`Failed to add answer: ${(error as AxiosError).message}`);
    }
  }

  async function addNotation(
    notation: NotationCreationAttributes,
  ): Promise<NotationAttributes> {
    try {
      const res = await axios.post<NotationAttributes>(
        baseURL +
          `/${notation.boardType.toLowerCase()}${notation.notationType.toLowerCase()}s/`,
        notation,
      );
      return res.data;
    } catch (error) {
      throw new Error(
        `Failed to add notation:${notation},  ${(error as AxiosError).message}`,
      );
    }
  }

  function getImageUrl(imageFileName: string): string {
    return imagesURL + "/" + imageFileName;
  }

  async function updateNotationValue(notation: NotationAttributes) {
    if ("value" in notation === false) return null;
    try {
      await axios.put<NotationAttributes>(
        baseURL +
          `/${notation.boardType.toLowerCase()}${notation.notationType.toLowerCase()}s`,
        {
          uuid: notation.uuid,
          value: (notation as any)["value"],
        },
      );
    } catch (error) {
      throw new Error(
        `Failed to update notation value: ${(error as AxiosError).message}`,
      );
    }
  }

  async function updateNotation(notation: NotationAttributes) {
    try {
      await axios.put<NotationAttributes>(
        baseURL +
          `/${notation.boardType.toLowerCase()}${notation.notationType.toLowerCase()}s`,
        notation,
      );
    } catch (error) {
      throw new Error(
        `Failed to update notation:  ${(error as AxiosError).message}`,
      );
    }
  }

  async function updateSqrtNotationAttributes(
    sqrtNotation: SqrtNotationAttributes,
  ) {
    try {
      return axios.put(
        baseURL +
          `/${sqrtNotation.boardType.toLowerCase()}${sqrtNotation.notationType.toLowerCase()}s`,
        {
          uuid: sqrtNotation.uuid,
          fromCol: sqrtNotation.fromCol,
          toCol: sqrtNotation.toCol,
          row: sqrtNotation.row,
        },
      );
    } catch (error) {
      throw new Error(
        `Failed to update sqrt notation attributes: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function updateLineNotationAttributes(
    lineNotation: LineNotationAttributes,
  ) {
    try {
      return axios.put(
        baseURL +
          `/${lineNotation.boardType.toLowerCase()}${lineNotation.notationType.toLowerCase()}s`,
        {
          uuid: lineNotation.uuid,
          p1x: lineNotation.p1x,
          p2x: lineNotation.p2x,
          p1y: lineNotation.p1y,
          p2y: lineNotation.p2y,
        },
      );
    } catch (error) {
      throw new Error(
        `Failed to update slope line notation attributes: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function updateCurveNotationAttributes(curve: CurveNotationAttributes) {
    try {
      return axios.put(
        baseURL +
          `/${curve.boardType.toLowerCase()}${curve.notationType.toLowerCase()}s`,
        {
          uuid: curve.uuid,
          p1x: curve.p1x,
          p2x: curve.p2x,
          p1y: curve.p1y,
          p2y: curve.p2y,
          cpx: curve.cpx,
          cpy: curve.cpy,
        },
      );
    } catch (error) {
      throw new Error(
        `Failed to update curve notation attributes: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function updateCircleNotationAttributes(
    circle: CircleNotationAttributes,
  ) {
    try {
      return axios.put(
        baseURL +
          `/${circle.boardType.toLowerCase()}${circle.notationType.toLowerCase()}s`,
        {
          uuid: circle.uuid,
          cx: circle.cx,
          cy: circle.cy,
          r: circle.r,
        },
      );
    } catch (error) {
      throw new Error(
        `Failed to update circle notation attributes: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function updateConicNotationAttributes(
    conic: ConicNotationAttributes,
  ) {
    try {
      return axios.put(
        baseURL +
          `/${conic.boardType.toLowerCase()}${conic.notationType.toLowerCase()}s`,
        {
          uuid: conic.uuid,
          kind: conic.kind,
          hx: conic.hx,
          hy: conic.hy,
          axis: conic.axis,
          a: conic.a,
          b: conic.b,
        },
      );
    } catch (error) {
      throw new Error(
        `Failed to update conic notation attributes: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function updateFreeSketchNotationAttributes(
    freeSketch: FreeSketchNotationAttributes,
  ) {
    try {
      return axios.put(
        baseURL +
          `/${freeSketch.boardType.toLowerCase()}${freeSketch.notationType.toLowerCase()}s`,
        {
          uuid: freeSketch.uuid,
          points: freeSketch.points,
        },
      );
    } catch (error) {
      throw new Error(
        `Failed to update free sketch notation attributes: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function getLesson(lessonUUId: string): Promise<LessonAttributes> {
    try {
      const { data } = await axios.get<LessonAttributes>(
        baseURL + "/lessons?lessonUUId=" + lessonUUId,
      );
      return data;
    } catch (error) {
      throw new Error(
        `Failed to get lesson ${lessonUUId}: ${(error as AxiosError).message}`,
      );
    }
  }

  async function getUser(userUUId: string): Promise<UserAttributes> {
    try {
      const { data } = await axios.get<UserAttributes>(
        baseURL + "/students?uuid=" + userUUId,
      );
      return data;
    } catch (error) {
      throw new Error(
        `Failed to get user ${userUUId}: ${(error as AxiosError).message}`,
      );
    }
  }

  async function getUserByEmail(email: string): Promise<UserAttributes> {
    try {
      const { data } = await axios.get<UserAttributes>(
        baseURL + "/users?email=" + email,
      );
      return data;
    } catch (error) {
      throw new Error(
        `Failed to get user ${email}: ${(error as AxiosError).message}`,
      );
    }
  }

  async function getUserByAccessToken(): Promise<UserAttributes | null> {
    try {
      const { data } = await axios.get<UserAttributes>(baseURL + "/users");
      return data;
    } catch (error) {
      return null;
    }
  }

  async function getQuestion(
    questionUUId: string,
  ): Promise<QuestionAttributes> {
    try {
      const { data } = await axios.get<QuestionAttributes>(
        baseURL + "/questions?uuid=" + questionUUId,
      );
      return data;
    } catch (error) {
      throw new Error(
        `Failed to get question ${questionUUId}: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function getAnswer(answerUUId: string): Promise<AnswerAttributes> {
    try {
      const { data } = await axios.get<AnswerAttributes>(
        baseURL + "/answers?uuid=" + answerUUId,
      );
      return data;
    } catch (error) {
      throw new Error(
        `Failed to get answer ${answerUUId}: ${(error as AxiosError).message}`,
      );
    }
  }

  async function getTeacherLessons(
    userUUId: string,
  ): Promise<LessonAttributes[]> {
    try {
      const { data } = await axios.get<LessonAttributes[]>(
        baseURL + "/lessons?userUUId=" + userUUId,
      );

      return data;
    } catch (error) {
      throw new Error(
        `Failed to get teacher lessons for user ${userUUId}: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function getStudentLessons(
    userUUId: string,
  ): Promise<StudentLessonAttributes[]> {
    try {
      const { data } = await axios.get<StudentLessonAttributes[]>(
        baseURL + "/studentlessons?userUUId=" + userUUId,
      );

      return data;
    } catch (error) {
      throw new Error(
        `Failed to get student lessons for user ${userUUId}: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function getQuestions(
    lessonUUId: string,
  ): Promise<QuestionAttributes[]> {
    try {
      const { data } = await axios.get<QuestionAttributes[]>(
        baseURL + "/questions?lessonUUId=" + lessonUUId,
      );
      return data;
    } catch (error) {
      throw new Error(
        `Failed to get questions for lesson ${lessonUUId}: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function getPracticeQuestions(
    subject?: string,
  ): Promise<PracticeQuestionListItem[]> {
    try {
      const query = subject
        ? `?subject=${encodeURIComponent(subject)}`
        : "";
      const { data } = await axios.get<PracticeQuestionListItem[]>(
        baseURL + "/practice-questions" + query,
      );
      return data;
    } catch (error) {
      throw new Error(
        `Failed to get practice questions: ${(error as AxiosError).message}`,
      );
    }
  }

  async function createPracticeInstance(input: {
    generatorId: string;
    grade: number;
    locale: PracticeLocale;
    seed: number;
  }): Promise<PracticeInstancePublic> {
    try {
      const { data } = await axios.post<PracticeInstancePublic>(
        baseURL + "/practice-instances",
        input,
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      const serverMessage = axiosError.response?.data?.error;
      throw new Error(
        serverMessage
          ? `Failed to start exercise: ${serverMessage}`
          : `Failed to start exercise: ${axiosError.message}`,
      );
    }
  }

  async function getPracticeInstance(
    id: string,
  ): Promise<PracticeInstancePublic | null> {
    try {
      const { data } = await axios.get<PracticeInstancePublic>(
        baseURL + "/practice-instances/" + id,
      );
      return data;
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        return null;
      }
      throw new Error(
        `Failed to load practice instance ${id}: ${(error as AxiosError).message}`,
      );
    }
  }

  async function adaptiveStep(input: {
    skillId: string;
    difficulty: PracticeDifficulty;
    locale: PracticeLocale;
    grade?: number | null;
    seed: number;
    showWorkedExample: boolean;
  }): Promise<{
    skillId: string;
    difficulty: PracticeDifficulty;
    workedExample: {
      prompt: string;
      expectedAnswer: string;
      explanation: string;
    } | null;
    instance: PracticeInstancePublic | null;
    templateUuid: string | null;
  }> {
    const { data } = await axios.post(
      baseURL + "/practice/adaptive-step",
      input,
    );
    return data;
  }

  async function createPracticeShareSession(input: {
    hostParticipantId: string;
    snapshot: PracticeShareSnapshot;
  }): Promise<PracticeShareRecord> {
    const { data } = await axios.post<PracticeShareRecord>(
      baseURL + "/practice-sessions",
      input,
    );
    return data;
  }

  async function getPracticeShareSession(
    sessionId: string,
  ): Promise<PracticeShareRecord> {
    const { data } = await axios.get<PracticeShareRecord>(
      baseURL + "/practice-sessions/" + sessionId,
    );
    return data;
  }

  async function claimPracticeShareSeat(
    sessionId: string,
    participantId: string,
  ): Promise<PracticeShareRecord> {
    try {
      const { data } = await axios.post<PracticeShareRecord>(
        baseURL + "/practice-sessions/" + sessionId + "/join",
        { participantId },
      );
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      if (axiosError.response?.status === 409) {
        throw new PracticeShareSeatTakenError();
      }
      throw error;
    }
  }

  async function applyPracticeShareOp(
    sessionId: string,
    op: PracticeShareOp,
  ): Promise<PracticeShareRecord> {
    const { data } = await axios.post<PracticeShareRecord>(
      baseURL + "/practice-sessions/" + sessionId + "/ops",
      op,
    );
    return data;
  }

  async function getPracticeMastery(): Promise<PracticeSkillStateRow[]> {
    const { data } = await axios.get<PracticeSkillStateRow[]>(
      baseURL + "/practice/mastery",
    );
    return data;
  }

  async function savePracticeMastery(input: {
    rows: PracticeSkillStateRow[];
    attempt?: {
      skillId: string;
      difficulty: PracticeDifficulty;
      correct: boolean;
      instanceId?: string;
      legacyUuid?: string;
    };
  }): Promise<PracticeSkillStateRow[]> {
    const { data } = await axios.post<PracticeSkillStateRow[]>(
      baseURL + "/practice/mastery",
      input,
    );
    return data;
  }

  async function importPracticeMastery(
    rows: PracticeSkillStateRow[],
  ): Promise<PracticeSkillStateRow[]> {
    const { data } = await axios.post<PracticeSkillStateRow[]>(
      baseURL + "/practice/mastery/import",
      { rows },
    );
    return data;
  }

  async function getPracticeQuestion(
    questionUUId: string,
  ): Promise<PracticeQuestionListItem | null> {
    try {
      const { data } = await axios.get<PracticeQuestionListItem>(
        baseURL + "/practice-questions/" + questionUUId,
      );
      return data;
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        return null;
      }
      throw new Error(
        `Failed to get practice question ${questionUUId}: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function getAnswers(questionUUId: string): Promise<AnswerAttributes[]> {
    try {
      const { data } = await axios.get<AnswerAttributes[]>(
        baseURL + "/answers?questionUUId=" + questionUUId,
      );

      return data;
    } catch (error) {
      throw new Error(
        `Failed to get answers for question ${questionUUId}: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function deleteNotation(notation: NotationAttributes) {
    try {
      return axios.delete(
        baseURL +
          `/${notation.boardType
            .toString()
            .toLowerCase()}${notation.notationType.toString().toLowerCase()}s`,
        { data: notation },
      );
    } catch (error) {
      throw new Error(
        `Failed to remove notation: ${(error as AxiosError).message}`,
      );
    }
  }

  async function getNotation<T extends NotationAttributes>(
    notationType: NotationType,
    boardType: BoardType,
    uuid: string,
  ): Promise<T> {
    try {
      const uri =
        baseURL +
        `/${boardType.toString().toLowerCase()}${notationType
          .toString()
          .toLowerCase()}/${uuid}`;
      const { data } = await axios.get<T>(uri);
      data.boardType = boardType;
      data.notationType = notationType;
      return data;
    } catch (error) {
      throw new Error(
        `Failed to get notation for ${uuid} ${boardType} ${notationType}: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  async function getNotations<T extends NotationAttributes>(
    notationType: NotationType,
    boardType: string,
    parentUUId: string,
  ): Promise<T[]> {
    try {
      const uri =
        baseURL +
        `/${boardType.toString().toLowerCase()}${notationType
          .toString()
          .toLowerCase()}s?uuid=${parentUUId}`;
      const { data } = await axios.get<T[]>(uri);
      return data;
    } catch (error) {
      throw new Error(
        `Failed to get notations for ${boardType} ${notationType}: ${
          (error as AxiosError).message
        }`,
      );
    }
  }

  return {
    getImageUrl,
    getNotation,
    getNotations,
    getAnswers,
    getQuestions,
    getPracticeQuestions,
    getPracticeQuestion,
    createPracticeInstance,
    getPracticeInstance,
    adaptiveStep,
    createPracticeShareSession,
    getPracticeShareSession,
    claimPracticeShareSeat,
    applyPracticeShareOp,
    getPracticeMastery,
    savePracticeMastery,
    importPracticeMastery,
    getStudentLessons,
    getTeacherLessons,
    getUser,
    getUserByEmail,
    getUserByAccessToken,
    getAnswer,
    getQuestion,
    getLesson,
    authGoogleUser,
    authLocalUserByPassword,
    registerUser,
    addLesson,
    updateLesson,
    deleteLesson,
    addLessonToSharedLessons,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    addAnswer,
    addNotation,
    updateSqrtNotationAttributes,
    updateLineNotationAttributes,
    updateCurveNotationAttributes,
    updateCircleNotationAttributes,
    updateConicNotationAttributes,
    updateFreeSketchNotationAttributes,
    updateNotationValue,
    updateNotation,
    deleteNotation,
    log,
    recognizeSketchOcr,
    getPracticeAiQuota,
    checkPracticeWork,
    coachPracticeWork,
    askPracticeQuestion,
    extractPracticeParts,
  };
}
